import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  InputAdornment,
  TextField,
  TablePagination,
  Switch,
  styled,
  IconButton,
} from "@mui/material";
import { CirclePlus, Search } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import DataTable from "../../components/DataTable";
import UserModal from "./components/UserModal";
import usePermissions from "./hooks/usePermissions";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  toggleUserActive,
} from "../../api/Authentication/api";

const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 50,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#124F3E",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#124F3E",
      border: "6px solid #fff",
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#FF5252",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const UserManagement = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [total, setTotal] = useState(0);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
      });

      if (response?.data) {
        const formattedUsers = response.data.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          status: user.is_active,
          role: user.role?.name || "No Role",
          roleId: user.role?.id,
          role_display: user.role?.name || "No Role", // Add this for compatibility
          is_active: user.is_active, // Add this for compatibility
        }));
        setUsers(formattedUsers);
        setTotal(response.total);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      showErrorToast(error, "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (can.viewUser()) {
      fetchUsers();
    }
  }, [page, rowsPerPage, searchQuery]);

  const handleToggleActive = async (userId) => {
    console.log("Toggling user status:", userId);
    try {
      setLoading(true);
      const response = await toggleUserActive(userId);
      if (response) {
        await fetchUsers();
        showSuccessToast(
          `User ${
            response.is_active ? "activated" : "deactivated"
          } successfully`
        );
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      showErrorToast(
        error?.response?.data?.message || "Failed to update user status"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUserSubmit = async (formData) => {
    try {
      if (selectedUser) {
        await updateUser(selectedUser.id, formData);
        showSuccessToast("User updated successfully");
      } else {
        const response = await createUser(formData);
        showSuccessToast(
          response?.data?.message || "User created successfully"
        );
      }
      setUserModalOpen(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (error) {
      console.error("Error submitting user:", error);
      showErrorToast(
        error?.response?.data?.message ||
          (selectedUser ? "Failed to update user" : "A user with this email already exists. Please use a different email.")
      );
    }
  };

  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    {
      id: "status",
      label: "Status",
      format: (value, row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CustomSwitch
            checked={Boolean(value)}
            disabled={!can.editUser()}
            onChange={() => {
              if (can.editUser() && row?.id) {
                handleToggleActive(row.id);
              }
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: value ? "#124F3E" : "#FF5252",
              fontWeight: 500,
              userSelect: "none",
            }}
          >
            {value ? "Active" : "Inactive"}
          </Typography>
        </Box>
      ),
    },
    { id: "role", label: "Role" },
  ];

  const customActions = (row) => (
    <>
      {can.editUser() && (
        <IconButton
          aria-label="edit"
          color="primary"
          onClick={() => {
            setSelectedUser(row);
            setUserModalOpen(true);
          }}
        >
          <EditIcon />
        </IconButton>
      )}
      {can.deleteUser() && (
        <IconButton
          aria-label="delete"
          color="secondary"
          onClick={() => {
            try {
              deleteUser(row.id);
              fetchUsers();
              showSuccessToast("User deleted successfully");
            } catch (error) {
              console.error("Error deleting user:", error);
              showErrorToast(error, "Failed to delete user");
            }
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </>
  );

  if (!can.viewUser()) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          You don't have permission to view this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        User Management
      </Typography>

      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />
        {can.addUser() && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => {
              setSelectedUser(null);
              setUserModalOpen(true);
            }}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add User
          </Button>
        )}
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={users}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        totalRows={total}
        onRowsPerPageChange={handleRowsPerPageChange}
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          ),
        }}
        customActions={customActions}
      />

      <UserModal
        open={userModalOpen}
        onClose={() => {
          setUserModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUserSubmit}
        user={selectedUser}
        mode={selectedUser ? "edit" : "add"}
      />

      <Toast />
    </Box>
  );
};

export default UserManagement;
